/**
 * Headcount, and the five readings it splits into.
 *
 * Spec 01 §12 asks for "Online/active/idle/offline". Four of those words are
 * available and one is not, and the missing one is the interesting one.
 *
 * **There is no "offline" count here, and that is a decision rather than a gap.**
 * Offline is a claim about a *machine*: `Device.liveness`, computed by Task 19 from
 * `last_heartbeat_at` on every read, never stored, lagging pessimistically. The only
 * endpoint that publishes it is `GET /devices`, behind `workforce.manage_devices` —
 * which is in the `ceo` floor and **not** in the `manager` floor, so a department
 * manager cannot read it at all. What a manager *can* read is Task 36's aggregates,
 * and those report observed time, not machine health.
 *
 * So this tile counts what the aggregates observed, in five values that are never
 * collapsed, and the device strip counts machines separately and says it is counting
 * machines. An employee who did not work and an employee whose agent died are
 * different people — `PRODUCT.md` §4's seventh row — and here they are
 * `no_activity_data` plus whatever the attendance column says beside them, not one
 * bucket labelled with a word about hardware.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { MessageKey } from '../../i18n/messages';
import { PRESENCE_READINGS, type PresenceCensus, type PresenceReading } from '../../overview/fold';
import { EmptyState } from '../../states/EmptyState';
import { PartialData } from '../../states/PartialData';
import { Tile } from './Tile';

export const PRESENCE_LABEL: Readonly<Record<PresenceReading, MessageKey>> = {
  active: 'overview.presence.active',
  idle: 'overview.presence.idle',
  observed_none: 'overview.presence.observed_none',
  not_computed: 'overview.presence.not_computed',
  no_activity_data: 'overview.presence.no_activity_data',
};

const PRESENCE_HELP: Readonly<Record<PresenceReading, MessageKey>> = {
  active: 'overview.presence.active.help',
  idle: 'overview.presence.idle.help',
  observed_none: 'overview.presence.observed_none.help',
  not_computed: 'overview.presence.not_computed.help',
  no_activity_data: 'overview.presence.no_activity_data.help',
};

export function PresenceTile({
  census,
  hasDayRows,
}: {
  readonly census: PresenceCensus;
  /** Whether the day read answered at all. `false` is not "nobody is present". */
  readonly hasDayRows: boolean;
}): ReactNode {
  const { t } = useI18n();

  if (census.headcount === 0) {
    return (
      <Tile
        title={t('overview.presence.title')}
        figure={
          <EmptyState
            title={t('overview.presence.empty')}
            description={t('overview.presence.empty.next')}
            testId="k4-presence-empty"
          />
        }
        testId="k4-presence"
      />
    );
  }

  return (
    <Tile
      title={t('overview.presence.title')}
      figure={
        // Display 26px is for **numbers** (`DESIGN.md` §6). The headcount is the
        // number; "nhân viên trong phạm vi" is its unit and is set at reading size
        // under it, because a sentence at 26px reads as a headline.
        <span className="wp-figure">{census.headcount}</span>
      }
      detail={
        <>
          <p className="wp-small wp-muted">{t('overview.presence.headcountUnit')}</p>
          <ul className="wp-breakdown">
            {PRESENCE_READINGS.map((reading) => (
              <li key={reading} data-testid={`k4-presence-${reading}`}>
                <span className="wp-breakdown__count">{census.counts[reading]}</span>{' '}
                <span title={t(PRESENCE_HELP[reading])}>{t(PRESENCE_LABEL[reading])}</span>
              </li>
            ))}
          </ul>
        </>
      }
      caveat={
        <>
          {/* Not a tooltip and not behind a disclosure: the absence of an "offline"
              count is the thing a reader is most likely to fill in for themselves. */}
          <p className="wp-small wp-muted" data-testid="k4-presence-no-offline">
            {t('overview.presence.noOffline')}
          </p>
          {hasDayRows ? null : (
            <p className="wp-small" data-testid="k4-presence-no-rows">
              {t('overview.presence.noRows')}
            </p>
          )}
          {census.partialRows > 0 ? (
            <PartialData excludes={t('overview.hours.excludes')} testId="k4-presence-partial" />
          ) : null}
        </>
      }
      // Luật Màu Có Nghĩa: the tint says "this figure needs somebody to do
      // something", and only partial rows do — a day the read has not answered for
      // yet is an empty tile, and an empty tile carries no colour (finish review,
      // round 2, fix 2). The missing-rows sentence is still printed in the caveat.
      attention={census.partialRows > 0}
      testId="k4-presence"
    />
  );
}
